import dayjs from "dayjs";
import { ArrowUp, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FC } from "react";

import { cn } from "@/lib/utils";

import {
  allDaySpans,
  formatTimeShort,
  layoutTimedEvents,
  timedEventsOnDay,
  earlierCounts,
  weekDays,
  type CalendarEvent
} from "../calendarModel";
import { EventBlock, EventPill } from "./EventChip";

export const HOUR_HEIGHT_PX = 56;
/** Denser geometry for the narrow Home rail. */
export const RAIL_HOUR_HEIGHT_PX = 48;
const SCROLL_TO_HOUR = 7;

export type TimeGridVariant = "default" | "rail";

const VARIANTS: Record<
  TimeGridVariant,
  { gutter: string; hourHeight: number; showDayHeader: boolean }
> = {
  default: {
    gutter: "w-16 shrink-0",
    hourHeight: HOUR_HEIGHT_PX,
    showDayHeader: true
  },
  rail: {
    gutter: "w-12 shrink-0",
    hourHeight: RAIL_HOUR_HEIGHT_PX,
    showDayHeader: false
  }
};

type Props = {
  /** "week" renders the anchor's Sunday-first week; "day" just the anchor. */
  mode: "week" | "day";
  anchor: Date;
  events: CalendarEvent[];
  highlightedId: string | null;
  onOpenEvent: (event: CalendarEvent) => void;
  /**
   * Click on an hour slot (timed) or the all-day row (all-day). Omit for a
   * read-only grid.
   */
  onCreateAt?: (start: Date, allDay: boolean) => void;
  now?: Date;
  /** "rail" drops the day header and tightens the gutter and hour rows. */
  variant?: TimeGridVariant;
  /** Hour (fractional allowed) scrolled into view on mount. */
  scrollToHour?: number;
};

const tzLabel = (d: Date): string => {
  const offsetMin = -d.getTimezoneOffset();
  const sign = offsetMin >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMin);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `GMT${sign}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
};

const HOURS = Array.from({ length: 24 }, (_, h) => h);

/** Shared week/day time grid: all-day row on top, 24h scrollable body below. */
export const TimeGridView: FC<Props> = ({
  mode,
  anchor,
  events,
  highlightedId,
  onOpenEvent,
  onCreateAt,
  now = new Date(),
  variant = "default",
  scrollToHour = SCROLL_TO_HOUR
}) => {
  const { gutter: GUTTER_CLASS, hourHeight, showDayHeader } = VARIANTS[variant];
  const canCreate = typeof onCreateAt === "function";
  const days = useMemo(
    () =>
      mode === "week"
        ? weekDays(anchor)
        : [dayjs(anchor).startOf("day").toDate()],
    [anchor, mode]
  );
  const spans = useMemo(() => allDaySpans(events, days), [days, events]);
  const laneCount = spans.reduce((max, s) => Math.max(max, s.lane + 1), 0);
  const layouts = useMemo(
    () =>
      days.map((day) => layoutTimedEvents(timedEventsOnDay(events, day), day)),
    [days, events]
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = scrollToHour * hourHeight - 8;
    setScrollTop(el.scrollTop);
  }, [hourHeight, mode, scrollToHour]);
  const earlier = useMemo(
    () =>
      variant === "default"
        ? earlierCounts(layouts, (scrollTop / hourHeight) * 60)
        : [],
    [hourHeight, layouts, scrollTop, variant]
  );

  const nowLineTop =
    (dayjs(now).diff(dayjs(now).startOf("day"), "minute") / (24 * 60)) * 100;
  const todayCol = days.findIndex((d) => dayjs(d).isSame(now, "day"));
  const gridTemplate = {
    gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`
  };

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      role="grid"
      aria-label={mode === "week" ? "Week" : "Day"}
    >
      {/* Header: timezone gutter + day labels */}
      {showDayHeader ? (
        <div className="flex h-9 shrink-0 border-b border-line-2">
          <div
            className={cn(
              GUTTER_CLASS,
              "flex items-center justify-end px-2 text-xs text-fg-4"
            )}
          >
            {tzLabel(now)}
          </div>
          <div className="grid flex-1" style={gridTemplate}>
            {days.map((day) => {
              const isToday = dayjs(day).isSame(now, "day");
              return (
                <div
                  key={day.toISOString()}
                  role="columnheader"
                  aria-label={dayjs(day).format("dddd, MMMM D")}
                  aria-current={isToday ? "date" : undefined}
                  className="flex items-center justify-center gap-1.5 border-l border-line-1 px-2 text-base"
                >
                  <span className={isToday ? "text-fg" : "text-fg-3"}>
                    {dayjs(day).format(mode === "week" ? "ddd" : "dddd")}
                  </span>
                  {isToday ? (
                    <span
                      className="inline-flex size-5.5 items-center justify-center rounded-full bg-fg text-sm font-medium text-surface tabular-nums"
                      data-testid="calendar-today-number"
                    >
                      {dayjs(day).format("D")}
                    </span>
                  ) : (
                    <span className="text-fg-2 tabular-nums">
                      {dayjs(day).format("D")}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* All-day row */}
      <div className="flex shrink-0 border-b border-line-2">
        <div
          className={cn(
            GUTTER_CLASS,
            "flex items-center justify-end px-2 text-xs text-fg-4"
          )}
        >
          All Day
        </div>
        <div
          className="relative grid min-h-9 flex-1"
          style={{
            ...gridTemplate,
            gridTemplateRows: `repeat(${Math.max(
              laneCount,
              1
            )}, minmax(0, auto))`
          }}
        >
          {days.map((day, col) => (
            <button
              type="button"
              tabIndex={-1}
              key={`bg-${day.toISOString()}`}
              data-all-day-slot={dayjs(day).format("YYYY-MM-DD")}
              aria-label={`New all-day entry on ${dayjs(day).format("MMM D")}`}
              disabled={!canCreate}
              onClick={() => onCreateAt?.(day, true)}
              className={cn(
                "border-l border-line-1",
                canCreate && "cursor-pointer hover:bg-fill-1"
              )}
              style={{
                gridColumn: col + 1,
                gridRow: `1 / span ${Math.max(laneCount, 1)}`
              }}
            />
          ))}
          {spans.map((s) => (
            <div
              key={s.event.id}
              className="pointer-events-none z-10 px-1 py-0.5"
              style={{
                gridColumn: `${s.startCol + 1} / span ${s.span}`,
                gridRow: s.lane + 1
              }}
            >
              <EventPill
                event={s.event}
                hideTime
                highlighted={highlightedId === `reminder-${s.event.id}`}
                onOpen={onOpenEvent}
                now={now}
                className={cn(
                  "pointer-events-auto",
                  s.clippedStart && "rounded-l-none",
                  s.clippedEnd && "rounded-r-none"
                )}
                leading={
                  s.clippedStart ? (
                    <ChevronLeft className="size-3 shrink-0 opacity-60" />
                  ) : null
                }
                trailing={
                  s.clippedEnd ? (
                    <ChevronRight className="ml-auto size-3 shrink-0 opacity-60" />
                  ) : null
                }
              />
            </div>
          ))}
        </div>
      </div>

      {/* Time grid */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        {earlier.some((e) => e.count > 0) ? (
          <div className="pointer-events-none absolute inset-x-0 top-1 z-30 flex">
            <div className={GUTTER_CLASS} />
            <div className="grid flex-1" style={gridTemplate}>
              {earlier.map((e, col) => (
                <div
                  key={days[col].toISOString()}
                  className="flex justify-center"
                >
                  {e.count > 0 ? (
                    <button
                      type="button"
                      className="pointer-events-auto inline-flex h-4.5 items-center gap-1 rounded-full bg-elevated px-1.5 text-xs text-fg-3 inset-ring inset-ring-line-2 hover:text-fg"
                      onClick={() => {
                        const el = scrollRef.current;
                        if (el)
                          el.scrollTop =
                            (e.firstTopPct / 100) * hourHeight * 24 - 8;
                      }}
                      data-testid="calendar-earlier-chip"
                    >
                      <ArrowUp className="size-2.5" />
                      {e.count} earlier
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto"
          onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
        >
          <div className="flex" style={{ height: hourHeight * 24 }}>
            <div className={cn(GUTTER_CLASS, "relative")}>
              {HOURS.map((h) =>
                h === 0 ? null : (
                  <div
                    key={h}
                    className="absolute right-2 -translate-y-1/2 text-xs text-fg-4"
                    style={{ top: h * hourHeight }}
                  >
                    {formatTimeShort(dayjs(anchor).hour(h).minute(0).toDate())
                      .replace("am", " AM")
                      .replace("pm", " PM")}
                  </div>
                )
              )}
            </div>
            <div className="relative grid flex-1" style={gridTemplate}>
              {days.map((day, col) => (
                <div
                  key={day.toISOString()}
                  role="gridcell"
                  aria-label={dayjs(day).format("dddd, MMMM D")}
                  className="relative border-l border-line-1"
                >
                  {HOURS.map((h) => (
                    <button
                      type="button"
                      tabIndex={-1}
                      key={h}
                      data-hour-slot={`${dayjs(day).format(
                        "YYYY-MM-DD"
                      )}T${String(h).padStart(2, "0")}`}
                      aria-label={`New entry at ${formatTimeShort(
                        dayjs(day).hour(h).minute(0).toDate()
                      )} on ${dayjs(day).format("MMM D")}`}
                      disabled={!canCreate}
                      onClick={(e) => {
                        if (!onCreateAt) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const half =
                          e.clientY - rect.top > rect.height / 2 ? 30 : 0;
                        onCreateAt(
                          dayjs(day).hour(h).minute(half).second(0).toDate(),
                          false
                        );
                      }}
                      className={cn(
                        "block w-full border-b border-line-1",
                        canCreate && "cursor-pointer hover:bg-fill-1"
                      )}
                      style={{ height: hourHeight }}
                    />
                  ))}
                  {layouts[col].map((l) => {
                    const widthPct = 100 / l.columns;
                    return (
                      <EventBlock
                        key={l.event.id}
                        event={l.event}
                        highlighted={highlightedId === `reminder-${l.event.id}`}
                        onOpen={onOpenEvent}
                        now={now}
                        compact={
                          l.heightPct <
                          ((0.75 * HOUR_HEIGHT_PX) / hourHeight / 24) * 100
                        }
                        style={{
                          top: `${l.topPct}%`,
                          height: `calc(${l.heightPct}% - 2px)`,
                          left: `calc(${l.column * widthPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`
                        }}
                      />
                    );
                  })}
                  {col === todayCol ? (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-x-0 z-20 flex items-center"
                      style={{ top: `${nowLineTop}%` }}
                    >
                      <span className="-ml-1 size-2 rounded-full bg-destructive" />
                      <span className="h-px flex-1 bg-destructive" />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
